import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faFolders, faMinus } from '@fortawesome/pro-regular-svg-icons';
import { OptionType, useModal } from '@europrocurement/flexy-components';
import {
    useDossierDocumentsCtx,
    useDossierValidatorsCtx,
    useSelectedDossierContext,
    VALIDATOR_TYPE_GREFFE,
} from '@europrocurement/l2d-modules';
import { useSnackbar } from 'notistack';
import DossierFormaliteProcessListRows from './DossierFormaliteProcessListRows';
import DossierFormaliteProcessListHeaderCells from './DossierFormaliteProcessListHeaderCells';
import DissociateFormalitiesForm from '../DissociateFormalitiesForm/DissociateFormalitiesForm';
import MoveFormalitiesForm from '../MoveFormalitiesToOtherDossierForm/MoveFormalitiesToOtherDossierForm';
import { useDossierProcesses, useDossierProcessesActionsAndRender } from '../../hooks';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';

export type DossierFormaliteProcessProps = {
    libelle: string;
    documentsCount: number;
    complete: boolean;
    optionsCount: number;
    externalUrl?: string;
    onExternalUrlClick: (externalUrl: string) => void;
    id: number;
};

export type DossierFormaliteProcessesListProps = {
    formalities: DossierFormaliteProcessProps[];
    isHovered?: boolean;
};

export type SelectedFormality = {
    id: number;
};

const DossierFormaliteProcessesList: React.FunctionComponent<DossierFormaliteProcessesListProps> =
    function (props) {
        const { formalities, isHovered = false } = props;
        const { palette } = useTheme();
        const { modalActions } = useModal();
        const { getDossierDocuments } = useDossierDocumentsCtx();
        const { dossier, refreshDossier, siblings } = useSelectedDossierContext();
        const { enqueueSnackbar } = useSnackbar();
        const { hasOnGoingProcesses, getCurrentProcessOfType, canChangeProcessStatus } =
            useDossierProcesses();
        const { actionUpdateValidatorStatus } = useDossierProcessesActionsAndRender();
        const process = getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE);
        const { getDossierValidatorById } = useDossierValidatorsCtx();
        const statuses = process
            ? getDossierValidatorById(process.validatorId)?.statuses || []
            : [];
        const actions = [];

        const filteredSiblings = siblings.filter((doss) => doss.id !== dossier.id);

        const [selectedFormalitiesIds, setSelectedFormalitiesIds] = useState<number[]>([]);

        const handleOnUpdateStatus = () => {
            if (!process || !canChangeProcessStatus(process)) {
                return;
            }

            modalActions.call(
                <UpdateValidatorForm
                    processing={false}
                    process={process}
                    onItemSelected={() => {
                        modalActions.close();
                        refreshDossier();
                    }}
                    statuses={statuses}
                />,
            );
        };

        const handleDissociate = () => {
            // check if dossier has running validators
            if (hasOnGoingProcesses(dossier.validationProcesses)) {
                enqueueSnackbar(
                    'Des processus de validation sont en cours sur le dossier, dissociation impossible',
                    { variant: 'error' },
                );
                return;
            }

            modalActions.call(
                <DissociateFormalitiesForm
                    formalitiesIds={selectedFormalitiesIds}
                    dossier={dossier}
                    documents={getDossierDocuments()}
                    onSubmitDone={refreshDossier}
                />,
            );
        };

        const handleMove = () => {
            // check if dossier has running validators
            if (hasOnGoingProcesses(dossier.validationProcesses)) {
                enqueueSnackbar(
                    'Des processus de validation sont en cours sur le dossier, déplacement impossible',
                    { variant: 'error' },
                );
                return;
            }
            const options = filteredSiblings.map((doss) => {
                const nbrFormalities = doss.formalites.length;

                return {
                    value: doss.id,
                    label: `${doss.numero} - ${doss.societeDenomination} - ${
                        nbrFormalities > 1
                            ? `${doss.formalites[0].libelle} + ${nbrFormalities - 1} `
                            : doss.formalites[0].libelle
                    }`,
                } as OptionType;
            });

            modalActions.call(
                <MoveFormalitiesForm
                    formalitiesIds={selectedFormalitiesIds}
                    dossier={dossier}
                    documents={getDossierDocuments()}
                    onSubmitDone={refreshDossier}
                    options={options}
                />,
            );
        };

        if (process && canChangeProcessStatus(process)) {
            actions.push(actionUpdateValidatorStatus(handleOnUpdateStatus));
        }

        return (
            <Box
                sx={{
                    borderCollapse: 'separate',
                    borderSpacing: '0 4px',
                }}
            >
                <Box>
                    <DossierFormaliteProcessListHeaderCells isHovered={isHovered} />
                </Box>

                {/* If selected rows it display the action menu */}
                {selectedFormalitiesIds.length > 0 && isHovered && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: '8px',
                            paddingLeft: '19px',
                        }}
                    >
                        <Box
                            sx={{
                                border: `${palette.primary.main} 1px solid`,
                                width: '20px',
                                height: '20px',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            onClick={() => setSelectedFormalitiesIds([])}
                        >
                            <FontAwesomeIcon icon={faMinus} />
                        </Box>
                        <Typography
                            variant="l2dcaption"
                            sx={{ display: 'block' }}
                        >
                            {selectedFormalitiesIds.length} sélection(s)
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                            }}
                            onClick={() => handleDissociate()}
                        >
                            <FontAwesomeIcon icon={faFolders} />
                            Dissocier
                        </Box>

                        {/* Move to an other dossier from the collection */}
                        {filteredSiblings.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleMove()}
                            >
                                <FontAwesomeIcon icon={faFileImport} />
                                Déplacer
                            </Box>
                        )}
                    </Box>
                )}

                <Box>
                    <DossierFormaliteProcessListRows
                        formalities={formalities}
                        isHovered={isHovered}
                        selectedFormalitiesIds={selectedFormalitiesIds}
                        setSelectedFormalitiesIds={setSelectedFormalitiesIds}
                        actions={actions}
                    />
                </Box>
            </Box>
        );
    };

export default DossierFormaliteProcessesList;
